import { useState } from "react";
import { Loader2, Mail, ShieldCheck } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const ACCENT = "#4FA3D9";
const EMAIL_RE = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;

const ERRORS = {
  400: "That code isn't right or has expired. Check it, or request a new one.",
  429: "Too many attempts. Please wait a few minutes and try again.",
  503: "Email delivery isn't available right now. Please try again later.",
};
const errorFor = (status, fallback) => ERRORS[status] || fallback;

const inputCls = "flex-1 rounded-full border border-white/12 bg-white/[0.03] px-5 py-3 text-base text-white placeholder-white/30 outline-none transition-colors focus:border-white/30";
const btnCls = "inline-flex items-center justify-center gap-2 rounded-full px-6 py-3 text-sm font-semibold uppercase tracking-[0.14em] text-white transition-opacity disabled:opacity-60";
const linkCls = "text-xs font-medium underline-offset-4 transition-colors hover:underline disabled:opacity-50";

// Shared verified-recipient email flow for every simulation's Practice Report.
// Step 1: request a code for the recipient inbox (owners emailing their verified account
// address skip straight to delivery). Step 2: confirm the code → report is emailed.
export const ReportEmailForm = ({ reportId, onSent }) => {
  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [step, setStep] = useState("email"); // email | code
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");

  const post = (path, body) => fetch(`${API}/simulation/report/${reportId}${path}`, {
    method: "POST", credentials: "include", headers: { "Content-Type": "application/json" }, body: JSON.stringify(body),
  });

  const deliver = async (payload) => {
    const res = await post("/email", payload);
    if (!res.ok) throw res;
    onSent(payload.email);
  };

  const requestCode = async () => {
    const to = email.trim();
    setError(""); setNotice("");
    if (!EMAIL_RE.test(to)) { setError("Please enter a valid email address."); return; }
    if (!reportId) { setError("Report is still preparing — please try again in a moment."); return; }
    setBusy(true);
    try {
      const res = await post("/email/request-code", { email: to });
      if (!res.ok) throw res;
      const d = await res.json();
      if (d.code_required === false) await deliver({ email: to });
      else { setStep("code"); setCode(""); }
    } catch (e) {
      setError(errorFor(e?.status, "We couldn't send the code just now. Please try again."));
    }
    setBusy(false);
  };

  const resendCode = async () => {
    setError(""); setNotice(""); setBusy(true);
    try {
      const res = await post("/email/request-code", { email: email.trim() });
      if (!res.ok) throw res;
      setCode(""); setNotice("A new code is on its way.");
    } catch (e) {
      setError(errorFor(e?.status, "We couldn't resend the code just now. Please try again."));
    }
    setBusy(false);
  };

  const confirm = async () => {
    setError(""); setNotice("");
    if (!/^\d{6}$/.test(code.trim())) { setError("Enter the 6-digit code from your email."); return; }
    setBusy(true);
    try {
      await deliver({ email: email.trim(), code: code.trim() });
    } catch (e) {
      setError(errorFor(e?.status, "We couldn't send your report just now. Please try again."));
    }
    setBusy(false);
  };

  if (step === "code") return (
    <div data-testid="report-email-code-step">
      <p className="mt-2 text-base leading-relaxed text-white/60">
        We've sent a 6-digit code to <span className="text-white/85" data-testid="report-email-code-recipient">{email.trim()}</span>. Enter it to receive your Practice Report there.
      </p>
      <div className="mt-4 flex flex-col gap-3 sm:flex-row">
        <input
          type="text" inputMode="numeric" autoComplete="one-time-code" maxLength={6} value={code}
          onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
          onKeyDown={(e) => e.key === "Enter" && confirm()}
          placeholder="6-digit code" aria-label="6-digit code" data-testid="report-email-code-input"
          className={`${inputCls} tracking-[0.3em]`}
        />
        <button onClick={confirm} disabled={busy} data-testid="report-email-verify-btn" className={btnCls} style={{ background: ACCENT }}>
          {busy ? <Loader2 size={16} className="animate-spin" /> : <ShieldCheck size={16} />}
          Confirm &amp; Send
        </button>
      </div>
      {error && <p className="mt-2 text-sm text-red-300/80" data-testid="report-email-error">{error}</p>}
      {notice && <p className="mt-2 text-sm text-white/60" data-testid="report-email-notice">{notice}</p>}
      <div className="mt-3 flex items-center gap-5">
        <button onClick={resendCode} disabled={busy} data-testid="report-email-resend-btn" className={linkCls} style={{ color: ACCENT }}>Resend code</button>
        <button onClick={() => { setStep("email"); setCode(""); setError(""); setNotice(""); }} disabled={busy} data-testid="report-email-change-btn" className={`${linkCls} text-white/50`}>Use a different email</button>
      </div>
      <p className="mt-2 text-xs text-white/40">The code expires in 10 minutes and can be used once.</p>
    </div>
  );

  return (
    <>
      <p className="mt-2 text-base leading-relaxed text-white/60">
        Send a copy to your email so you can revisit what you practised and what you could strengthen.
      </p>
      <div className="mt-4 flex flex-col gap-3 sm:flex-row">
        <input
          type="email" value={email} onChange={(e) => setEmail(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && requestCode()}
          placeholder="you@example.com" data-testid="report-email-input" className={inputCls}
        />
        <button onClick={requestCode} disabled={busy} data-testid="report-email-btn" className={btnCls} style={{ background: ACCENT }}>
          {busy ? <Loader2 size={16} className="animate-spin" /> : <Mail size={16} />}
          Email My Practice Report
        </button>
      </div>
      {error && <p className="mt-2 text-sm text-red-300/80" data-testid="report-email-error">{error}</p>}
      <p className="mt-2 text-xs text-white/40">We'll send a one-time code to confirm this address before delivering your report.</p>
    </>
  );
};

export default ReportEmailForm;
